import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { formatInr, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Ticket, Plus, Ban, IndianRupee } from "lucide-react";
import { PAYMENT_METHODS, STATUS_TONE, fetchShopOwners } from "./shared";

export default function CouponTopupsPage() {
    const [rows, setRows] = useState(null);
    const [filters, setFilters] = useState({ merchant_user_id: "", status: "" });
    const [open, setOpen] = useState(false);

    const load = async () => {
        try {
            const params = new URLSearchParams({ limit: "200" });
            if (filters.merchant_user_id) params.set("merchant_user_id", filters.merchant_user_id);
            if (filters.status) params.set("status", filters.status);
            const res = await api.get(`/admin/coupon-topups?${params.toString()}`);
            setRows(res.data.topups);
        } catch (e) {
            toast.error("Could not load top-ups");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="topups-page">
            <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-orange-600">Extras</div>
                    <h1 className="text-3xl font-extrabold text-neutral-900">Coupon top-ups</h1>
                    <p className="mt-1 text-sm text-neutral-500">Sold when merchants exhaust their monthly package quota.</p>
                </div>
                <Button className="nest-gradient text-white" onClick={() => setOpen(true)} data-testid="sell-topup-btn">
                    <Plus size={16} className="mr-2" /> Sell top-up
                </Button>
            </header>

            <Card className="rounded-2xl border border-neutral-200">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px] text-sm" data-testid="topups-table">
                        <thead>
                            <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
                                <th className="px-4 py-3">Merchant ID</th>
                                <th className="px-4 py-3 text-right">Extras</th>
                                <th className="px-4 py-3 text-right">Used</th>
                                <th className="px-4 py-3 text-right">Price</th>
                                <th className="px-4 py-3">Purchased</th>
                                <th className="px-4 py-3">Valid until</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows === null &&
                                Array.from({ length: 4 }).map((_, i) => (
                                    <tr key={i}>
                                        {Array.from({ length: 8 }).map((_, j) => (
                                            <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>
                                        ))}
                                    </tr>
                                ))}
                            {rows?.map((t) => (
                                <TopupRow key={t.id} topup={t} onChanged={load} />
                            ))}
                            {rows?.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="px-4 py-10 text-center text-sm text-neutral-500">No top-ups yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <SellTopupDialog open={open} onOpenChange={setOpen} onCreated={() => { setOpen(false); load(); }} />
        </div>
    );
}

function TopupRow({ topup, onChanged }) {
    const doVoid = async () => {
        try {
            await api.post(`/admin/coupon-topups/${topup.id}/void`);
            toast.success("Voided");
            onChanged?.();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    return (
        <tr className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`topup-row-${topup.id}`}>
            <td className="px-4 py-3 font-mono text-xs text-neutral-500">{topup.merchant_user_id?.slice(0, 8)}…</td>
            <td className="px-4 py-3 text-right">{topup.extra_coupons}</td>
            <td className="px-4 py-3 text-right">{topup.used_count}</td>
            <td className="px-4 py-3 text-right font-mono">{formatInr(topup.price_paise)}</td>
            <td className="px-4 py-3 text-xs text-neutral-500">{formatDate(topup.purchased_at)}</td>
            <td className="px-4 py-3 text-xs text-neutral-500">{formatDate(topup.valid_until)}</td>
            <td className="px-4 py-3">
                <Badge variant="outline" className={STATUS_TONE[topup.status] || STATUS_TONE.EXHAUSTED}>{topup.status}</Badge>
            </td>
            <td className="px-4 py-3 text-right">
                {topup.status === "ACTIVE" && (
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" data-testid={`topup-void-${topup.id}`}>
                                <Ban size={14} className="mr-1" /> Void
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Void this top-up?</AlertDialogTitle>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Back</AlertDialogCancel>
                                <AlertDialogAction onClick={doVoid}>Void</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                )}
            </td>
        </tr>
    );
}

function SellTopupDialog({ open, onOpenChange, onCreated }) {
    const [presets, setPresets] = useState([]);
    const [merchants, setMerchants] = useState([]);
    const [merchantsError, setMerchantsError] = useState("");
    const [merchantId, setMerchantId] = useState("");
    const [extras, setExtras] = useState("");
    const [rupees, setRupees] = useState("");
    const [validUntil, setValidUntil] = useState("");
    const [payMethod, setPayMethod] = useState("UPI");
    const [payRef, setPayRef] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setMerchantsError("");
        fetchShopOwners()
            .then((list) => setMerchants(Array.isArray(list) ? list : []))
            .catch((err) => {
                const status = err?.response?.status;
                const msg =
                    status === 403
                        ? "You don't have permission to load the merchant list."
                        : "Failed to load merchants. Please try again.";
                setMerchantsError(msg);
                setMerchants([]);
                toast.error(msg);
            });
        api.get("/admin/coupon-topups/presets").then((r) => setPresets(r.data.presets || [])).catch(() => {});
        setValidUntil(new Date(Date.now() + 60 * 86400 * 1000).toISOString().slice(0, 10));
    }, [open]);

    const applyPreset = (p) => {
        setExtras(String(p.extra_coupons));
        setRupees(String(p.price_paise / 100));
    };

    const submit = async () => {
        if (!merchantId) { toast.error("Pick a merchant"); return; }
        const n = Number(extras);
        const p = Math.round(Number(rupees) * 100);
        if (!n || n < 1) { toast.error("Extras must be ≥1"); return; }
        try {
            setBusy(true);
            await api.post("/admin/coupon-topups", {
                merchant_user_id: merchantId,
                extra_coupons: n,
                price_paise: p,
                valid_until: validUntil || null,
                payment_method: payMethod,
                payment_reference: payRef || null,
                notes: notes || null,
            });
            toast.success("Top-up sold");
            setMerchantId(""); setExtras(""); setRupees(""); setPayRef(""); setNotes("");
            onCreated?.();
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl" data-testid="sell-topup-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Ticket size={16} className="text-orange-600" /> Sell coupon top-up</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-xs">Merchant *</Label>
                        <Select value={merchantId || "__none__"} onValueChange={(v) => setMerchantId(v === "__none__" ? "" : v)} disabled={!!merchantsError}>
                            <SelectTrigger data-testid="topup-merchant"><SelectValue placeholder="Pick a shop owner" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">— Select —</SelectItem>
                                {merchants.filter((m) => m.status === "ACTIVE").slice(0, 50).map((m) => (
                                    <SelectItem key={m.id} value={m.id}>{m.business_name || m.full_name} · {m.mobile_number || m.email}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {merchantsError && (
                            <div
                                className="mt-1 rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700"
                                data-testid="topup-merchants-error"
                            >
                                {merchantsError}
                            </div>
                        )}
                    </div>
                    {presets.length > 0 && (
                        <div>
                            <Label className="text-xs">Presets</Label>
                            <div className="mt-1 flex flex-wrap gap-2" data-testid="topup-presets">
                                {presets.map((p) => (
                                    <button
                                        key={p.slug}
                                        type="button"
                                        onClick={() => applyPreset(p)}
                                        data-testid={`topup-preset-${p.slug}`}
                                        className="rounded-xl border border-neutral-300 bg-white px-3 py-2 text-left text-xs font-semibold hover:border-orange-400 hover:bg-orange-50"
                                    >
                                        {p.label} · <span className="text-orange-700">{formatInr(p.price_paise)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Extra coupons *</Label>
                            <Input type="number" min={1} value={extras} onChange={(e) => setExtras(e.target.value)} data-testid="topup-extras" />
                        </div>
                        <div>
                            <Label className="text-xs">Price (₹) *</Label>
                            <div className="relative">
                                <IndianRupee size={14} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
                                <Input className="pl-7" type="number" value={rupees} onChange={(e) => setRupees(e.target.value)} data-testid="topup-price" />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Valid until</Label>
                            <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="topup-valid-until" />
                        </div>
                        <div>
                            <Label className="text-xs">Payment method</Label>
                            <Select value={payMethod} onValueChange={setPayMethod}>
                                <SelectTrigger data-testid="topup-method"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {PAYMENT_METHODS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Payment reference</Label>
                            <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} data-testid="topup-ref" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Notes</Label>
                            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="topup-notes" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button className="nest-gradient text-white" disabled={busy || !!merchantsError} onClick={submit} data-testid="topup-submit">
                        {busy ? "Selling…" : "Sell top-up"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
